import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getPublicBlogBySlug, listPublicBlogs } from '../api/publicBlogs';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';
import { renderMarkdown } from '../utils/markdown';
import { getStaticBlogBySlug, STATIC_BLOGS } from '../../content/staticBlogs.js';

const BlogDetail = () => {
  const { slug } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ['public-blog', slug],
    queryFn: () => getPublicBlogBySlug(slug),
    enabled: Boolean(slug),
  });
  const remoteBlog = data?.data || data || null;
  const blog = remoteBlog || getStaticBlogBySlug(slug);
  const { data: relatedData } = useQuery({
    queryKey: ['public-blogs-related', slug],
    queryFn: () => listPublicBlogs({ limit: 6, page: 1 }),
    enabled: Boolean(slug),
  });

  if (isLoading) {
    return <div className="mx-auto max-w-4xl px-4 py-10 text-sm text-slate-500">Loading blog...</div>;
  }

  if ((error && !blog) || !blog) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10 text-sm text-slate-500">
        Blog not found. <Link to="/blog" className="text-emerald-600">Back to blog</Link>
      </div>
    );
  }

  const publishedLabel = blog.published_at || blog.created_at
    ? new Date(blog.published_at || blog.created_at).toLocaleDateString()
    : 'Blog';
  const heroImage = blog.image_url ? resolveAssetUrl(blog.image_url) : '';
  const words = String(blog.body || '')
    .replace(/<[^>]*>/g, ' ')
    .split(/\s+/)
    .filter(Boolean).length;
  const readingTime = Math.max(1, Math.round(words / 180));
  const relatedSource =
    (relatedData?.data || relatedData?.items || relatedData || []).length
      ? (relatedData?.data || relatedData?.items || relatedData || [])
      : STATIC_BLOGS;
  const relatedItems = relatedSource
    .filter((item) => item && item.slug !== blog.slug)
    .slice(0, 3);

  return (
    <div className="bg-[#F7F2EA]">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-[#F7F2EA] to-[#F7F2EA]" />
        <div className="absolute -right-24 top-8 h-48 w-48 rounded-full bg-amber-100/70 blur-3xl" />
        <div className="absolute -left-20 bottom-0 h-40 w-40 rounded-full bg-slate-200/60 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 pt-10 pb-6">
          <Link to="/blog" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            ← Back to blog
          </Link>
          <p className="mt-4 text-[11px] uppercase tracking-[0.3em] text-amber-600">Homevilas blog</p>
          <h1 className="mt-3 text-3xl font-semibold text-slate-900 sm:text-4xl">
            {blog.title}
          </h1>
          {blog.excerpt ? (
            <p className="mt-3 text-base text-slate-600">{blog.excerpt}</p>
          ) : null}
          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.2em] text-slate-400">
            <span>{publishedLabel}</span>
            <span className="h-1 w-1 rounded-full bg-slate-400" />
            <span>{readingTime} min read</span>
            <span className="h-1 w-1 rounded-full bg-slate-400" />
            <span>Homevilas Journal</span>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 pb-12">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 shadow-[0_18px_40px_rgba(15,23,42,0.1)]">
          {heroImage ? (
            <img src={heroImage} alt={blog.title} className="h-72 w-full object-cover sm:h-96" loading="lazy" />
          ) : (
            <div className="flex h-72 items-center justify-center text-xs text-slate-400">No image</div>
          )}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,280px)]">
          <article
            className="blog-content rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-[0_14px_32px_rgba(15,23,42,0.08)] sm:p-8"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(blog.body || '') }}
          />
          <aside className="space-y-4">
            <div className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-[0_14px_32px_rgba(15,23,42,0.08)]">
              <p className="text-xs uppercase tracking-[0.28em] text-amber-600">Related posts</p>
              <div className="mt-4 space-y-3">
                {relatedItems.length ? (
                  relatedItems.map((item) => (
                    <Link
                      key={item.id}
                      to={`/blog/${item.slug}`}
                      className="group block rounded-[1.6rem] border border-slate-200/80 bg-[#FCFAF6] p-4 transition hover:-translate-y-0.5 hover:border-amber-200 hover:bg-white hover:shadow-[0_14px_28px_rgba(15,23,42,0.08)]"
                    >
                      <div className="overflow-hidden rounded-[1.2rem] bg-slate-100">
                        {item.image_url ? (
                          <img
                            src={resolveAssetUrl(item.image_url)}
                            alt={item.title}
                            className="h-40 w-full object-cover transition duration-500 group-hover:scale-105"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex h-40 items-center justify-center text-[10px] uppercase tracking-[0.18em] text-slate-400">
                            Blog
                          </div>
                        )}
                      </div>
                      <div className="mt-4">
                        <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-amber-600/80">
                          {item.published_at || item.created_at
                            ? new Date(item.published_at || item.created_at).toLocaleDateString()
                            : 'Blog'}
                        </p>
                        <p className="mt-2 line-clamp-2 text-[17px] font-semibold leading-7 text-slate-900">
                          {item.title}
                        </p>
                        <span className="mt-3 inline-flex items-center text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 transition group-hover:text-amber-700">
                          Read post
                        </span>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No related posts yet.</p>
                )}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-[0_14px_32px_rgba(15,23,42,0.08)]">
              <p className="text-xs uppercase tracking-[0.28em] text-slate-400">Need help?</p>
              <h3 className="mt-2 text-lg font-semibold text-slate-900">Talk to a travel concierge</h3>
              <p className="mt-2 text-sm text-slate-600">
                Personalized itineraries, verified stays, and on-trip support tailored to your plans.
              </p>
              <a
                href="/contact"
                className="mt-4 inline-flex items-center rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white"
              >
                Request a call
              </a>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
